/** Point d'entrée : chargement du contenu, routeur par hash, rendu des écrans, délégation des événements. */
import { ledgerHtml } from "./components/ledger.js";
import { measureCard, trajectoryTable } from "./components/measure-card.js";
import { bindSourcePanel } from "./components/source-info.js";
import { escapeHtml as e, int, num, typo } from "./format.js";
import { engineFallbackReason, missionCutGroups } from "./engine-adapter.js";
import { fixtureContent, loadContent } from "./loader.js";
import { setFactcheck } from "./factcheck.js";
import { estimateCard, lastEstimates, UNAVAILABLE_MSG } from "./components/proposal-card.js";
import { consentCard } from "./components/consent-card.js";
import type { MeasureCategory } from "../../engine/src/types.js";
import { apiHealth } from "./api.js";
import {
  deleteProposal,
  EstimateRateLimitedError,
  EstimateRefusedError,
  EstimateUnavailableError,
  estimateMeasure,
  saveProposal,
  startDictation,
} from "./proposals.js";
import { configureTelemetry, flush, hasConsent, setConsent, track } from "./telemetry.js";
import { renderExiste, renderExisteBottom } from "./screens/existe.js";
import { cmpHtml, gapHtml, leverPolHtml, missionPreviewHtml, priceHtml, rabotPolHtml, renderFinancement, renderFinancementBottom } from "./screens/financement.js";
import { renderMandat } from "./screens/mandat.js";
import { renderProgramme, renderProgrammeBottom } from "./screens/programme.js";
import { renderResultat } from "./screens/resultat.js";
import { paramValue, renderHypotheses } from "./screens/hypotheses.js";
import { influentialCovered, paramInfo } from "./params-info.js";
import { neutralityHit } from "./neutrality.js";
import { copyToClipboard, parseHash } from "./share.js";
import { Store, type AttrMetric, type Screen } from "./state.js";

const SCREENS: Screen[] = ["existe", "programme", "financement", "mandat", "resultat"];
const TITLES: Record<Screen | "hypotheses", string> = { existe: "Ce qui existe", programme: "Programme", financement: "Financement", mandat: "Mandat", resultat: "Résultat", hypotheses: "Hypothèses" };

/** Insère un fragment HTML après la passe typographique (espaces insécables, ADR 0018). */
function setHtml(el: HTMLElement, html: string): void {
  el.innerHTML = typo(html);
}

function $(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`#${id} manquant`);
  return el;
}

function currentScreen(): Screen | "hypotheses" {
  const { screen } = parseHash(location.hash);
  if (screen === "verdict") return "resultat"; // liens v0.1
  if (screen === "hypotheses") return "hypotheses";
  return (SCREENS as string[]).includes(screen) ? (screen as Screen) : "existe";
}

/** Dernier écran de la boucle affiché (retour depuis « Hypothèses »). */
let lastFlow: Screen = "existe";

function go(screen: Screen | "hypotheses") {
  if (currentScreen() === screen) render();
  else location.hash = `#${screen}`;
  window.scrollTo(0, 0);
}

let store: Store;
/** L'API annonce l'observatoire (`/api/health` → telemetry) : la carte de consentement est affichée. */
let observatory = false;
/** Dernier résultat de mandat envoyé (évite les doublons lors des re-rendus de l'écran résultat). */
let lastResultSent = "";
const app = () => $("app");
const bottom = () => $("bottom");

/** Garde : on ne peut pas sauter une étape. */
function guard(screen: Screen): Screen {
  const st = store.state;
  if (screen === "mandat" && !st.voted) return "financement";
  if (screen === "resultat" && !st.voted) return "programme";
  if (screen === "resultat" && !store.mandateDone) return "mandat";
  if ((screen === "financement" || screen === "programme" || screen === "existe") && st.voted) return store.mandateDone ? "resultat" : "mandat";
  return screen;
}

/** Dépliants à conserver d'un rendu à l'autre (catégorie, « Concrètement », « Effet sur 5 ans »…). */
function detailsKey(d: HTMLDetailsElement): string | null {
  for (const k of ["cat", "concr", "traj", "prop"]) if (d.dataset[k]) return `${k}:${d.dataset[k]}`;
  if (d.id) return `id:${d.id}`;
  return null;
}

let lastScreen: Screen | "hypotheses" | null = null;

/**
 * Bandeau de statut (ADR 0011) : présent tant que params.sources.json ne couvre pas, par une source publiée, tous les
 * paramètres déclarés influents. Aucun paramètre déclaré influent → toujours affiché.
 */
function renderBanner() {
  const el = $("status-banner");
  if (influentialCovered(store.content.params, store.content.paramSources)) {
    el.hidden = true;
    el.innerHTML = "";
    return;
  }
  el.hidden = false;
  el.innerHTML = `<span>Prototype — certains paramètres sont des hypothèses de jeu non sourcées</span> · <a href="#hypotheses" data-action="go-hypotheses">Voir les hypothèses</a>`;
}

/** Avis de lecture d'un lien (mesure retirée par le filtre de neutralité…), en tête de chaque écran jusqu'à « Rejouer ». */
function noticesHtml(): string {
  return store.shareNotices.map((n) => `<div class="card notice" role="status"><p class="sm warn">${e(n)}</p></div>`).join("");
}

function render() {
  const wanted0 = currentScreen();
  if (wanted0 === "hypotheses") {
    const a = app();
    const same = lastScreen === "hypotheses";
    const keep = new Map<string, boolean>();
    if (same) a.querySelectorAll<HTMLDetailsElement>("details[data-hypg]").forEach((d) => keep.set(d.dataset.hypg!, d.open));
    const y = window.scrollY;
    lastScreen = "hypotheses";
    setHtml(a, noticesHtml() + renderHypotheses(store));
    bottom().hidden = true;
    if (same) {
      a.querySelectorAll<HTMLDetailsElement>("details[data-hypg]").forEach((d) => {
        if (keep.has(d.dataset.hypg!)) d.open = keep.get(d.dataset.hypg!)!;
      });
      window.scrollTo(0, y);
    }
    document.title = `Moi aussi président — ${TITLES.hypotheses}`;
    return;
  }
  const wanted = wanted0;
  const screen = guard(wanted);
  if (screen !== wanted || parseHash(location.hash).screen === "verdict") {
    location.hash = `#${screen}`;
    return;
  }
  const a = app();
  const b = bottom();
  // Même écran : on garde les dépliants ouverts/fermés et la position de défilement.
  const same = lastScreen === screen;
  const keep = new Map<string, boolean>();
  if (same) a.querySelectorAll<HTMLDetailsElement>("details").forEach((d) => {
    const k = detailsKey(d);
    if (k) keep.set(k, d.open);
  });
  const y = window.scrollY;
  lastScreen = screen;
  lastFlow = screen;
  switch (screen) {
    case "existe":
      setHtml(a, renderExiste(store));
      setHtml(b, renderExisteBottom(store));
      b.hidden = false;
      break;
    case "programme":
      setHtml(a, renderProgramme(store));
      setHtml(b, renderProgrammeBottom(store));
      b.hidden = false;
      break;
    case "financement":
      setHtml(a, renderFinancement(store));
      setHtml(b, renderFinancementBottom(store));
      b.hidden = false;
      break;
    case "mandat":
      setHtml(a, renderMandat(store));
      b.hidden = true;
      break;
    case "resultat":
      setHtml(a, renderResultat(store));
      b.hidden = true;
      trackResult();
      break;
  }
  if (store.shareNotices.length) a.insertAdjacentHTML("afterbegin", typo(noticesHtml()));
  if (observatory && (screen === "programme" || screen === "resultat")) a.insertAdjacentHTML("beforeend", typo(consentCard()));
  if (same) {
    a.querySelectorAll<HTMLDetailsElement>("details").forEach((d) => {
      const k = detailsKey(d);
      if (k && keep.has(k)) d.open = keep.get(k)!;
    });
    a.querySelectorAll<HTMLDetailsElement>("details.traj-d[open]").forEach(fillTrajectory);
    window.scrollTo(0, y);
  }
  document.title = `Moi aussi président — ${TITLES[screen]}`;
}

function fillTrajectory(d: HTMLDetailsElement) {
  const id = d.dataset.traj!;
  const box = d.querySelector<HTMLElement>(".trajbox");
  if (!box) return;
  try {
    const pts = store.engine.measureTrajectory(store.ctx, id, store.state.programme.hyp);
    setHtml(box, trajectoryTable(pts));
  } catch (err) {
    box.innerHTML = `<p class="hint warn">Trajectoire indisponible : ${e(String(err))}</p>`;
  }
}

/** Dictée en cours (une à la fois). */
let stopDictation: (() => void) | null = null;

async function submitProposal(cat: MeasureCategory, dictated: boolean) {
  const ta = document.getElementById(`prop-${cat}`) as HTMLTextAreaElement | null;
  const out = document.getElementById(`propout-${cat}`);
  if (!ta || !out) return;
  const text = ta.value.trim();
  if (!text) {
    out.innerHTML = `<p class="hint warn">Écris ou dicte d'abord ta proposition.</p>`;
    return;
  }
  saveProposal(text, cat, dictated);
  lastEstimates.delete(cat);
  try {
    const est = await estimateMeasure(text, cat);
    // Défense en profondeur : le serveur filtre déjà, le navigateur refait le contrôle de neutralité (ADR 0011).
    if (neutralityHit(est.measure, store.content.politicalNames) !== null) {
      out.innerHTML = typo(estimateCard(null, "Proposition non chiffrée : elle nomme un parti ou une personnalité politique. Décris la mesure par son contenu."));
      return;
    }
    lastEstimates.set(cat, { est });
    out.innerHTML = typo(estimateCard(est, UNAVAILABLE_MSG, { added: store.state.programme.measure_ids.includes(est.measure.id) }));
    track({ type: "measure_dictated", text: text.slice(0, 500), category: cat, estimate_key: est.key });
  } catch (err) {
    // Pas d'API / chiffrage non ouvert → carte désactivée, libellé d'origine. Sinon : message du serveur, affiché tel quel.
    let msg = UNAVAILABLE_MSG;
    if (err instanceof EstimateRefusedError) msg = `Proposition non chiffrée : ${err.message}`;
    else if (err instanceof EstimateRateLimitedError) msg = err.message;
    else if (err instanceof EstimateUnavailableError && err.transient) msg = `Chiffrage IA momentanément indisponible : ${err.message}`;
    out.innerHTML = typo(estimateCard(null, msg));
    if (err instanceof EstimateRefusedError) track({ type: "measure_dictated", text: text.slice(0, 500), category: cat, accepted: false });
  }
  // La liste « Mes propositions » se met à jour sans perdre la carte affichée.
  const card = out.innerHTML;
  render();
  const again = document.getElementById(`propout-${cat}`);
  if (again) again.innerHTML = card;
  const d = again?.closest<HTMLDetailsElement>("details.cat");
  if (d) d.open = true;
}

/** Résultat du mandat pour l'observatoire (une fois par partie terminée, seulement avec consentement). */
function trackResult() {
  // Partie jouée avec des hypothèses modifiées : hors observatoire (les agrégats supposent les params publiés, PRD §13).
  if (!hasConsent() || !store.mandateDone || store.hypModified) return;
  const s = store.state;
  const key = JSON.stringify(store.toShare());
  if (key === lastResultSent) return;
  lastResultSent = key;
  const tl = store.timelineAll;
  const last = tl.years[tl.years.length - 1];
  const kpis: Record<string, number> = {};
  for (const [k, v] of Object.entries(last?.kpis ?? {})) if (Number.isFinite(v) && /^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(k) && k.length <= 64) kpis[k] = v;
  track({
    type: "mandate_result",
    seed: Math.trunc(s.seed),
    programme: s.programme.measure_ids.slice(0, 80),
    hyp: Object.fromEntries(Object.entries(s.programme.hyp).filter(([id, v]) => s.programme.measure_ids.includes(id) && Number.isFinite(v))),
    choices: s.choices,
    kpis,
  });
  void flush();
}

/** Financement voté (part du besoin couverte par des hausses de recettes, le reste par la dette ou le rabot). */
function trackFinancing() {
  const f = store.state.financing;
  const gap = store.engine.needGap(store.ctx, store.state.programme);
  const raised = Object.values(f.revenue_raises).reduce((x, y) => x + y, 0);
  const share = gap > 0 ? Math.max(0, Math.min(1, raised / gap)) : 1;
  const levers: Record<string, number> = { ...f.revenue_raises };
  if (f.across_the_board_cut) levers["rabot"] = f.across_the_board_cut;
  track({ type: "financing_chosen", revenue_share: share, levers });
}

/** Mise à jour légère de l'écran financement pendant le glissement d'un curseur (pas de re-rendu complet). */
function refreshFinancement() {
  const p = store.engine.previewFinancing(store.ctx, store.enginePrg, store.state.financing, store.state.seed);
  const gap = document.getElementById("gapbox");
  const cmp = document.getElementById("cmpbox");
  const l = document.getElementById("ledgerbox");
  if (gap) setHtml(gap, gapHtml(store, p));
  if (cmp) setHtml(cmp, cmpHtml(p));
  if (l) setHtml(l, ledgerHtml(p.first_year, store.engine.name));
  const pr = document.getElementById("pricebox");
  if (pr) setHtml(pr, priceHtml(store));
  for (const lv of store.content.levers) {
    const el = document.getElementById(`lvpol-${lv.id}`);
    if (el) setHtml(el, leverPolHtml(store, lv.id));
  }
  const rp = document.getElementById("rabotpol");
  if (rp) setHtml(rp, rabotPolHtml(store, p.rabot_bn_per_pt));
  if (store.engine.caps.missionCuts) {
    for (const g of missionCutGroups(store.content.params)) {
      const el = document.getElementById(`mcprev-${g.id}`);
      if (el) setHtml(el, missionPreviewHtml(store, g));
    }
  }
  setHtml(bottom(), renderFinancementBottom(store));
}

function bindEvents() {
  const a = app();
  const b = bottom();
  const onAction = async (action: string, target: HTMLElement) => {
    switch (action) {
      case "reset":
        store.reset();
        go("programme");
        break;
      case "load-preset":
        if (target.dataset.preset && store.loadPreset(target.dataset.preset)) go("programme");
        break;
      case "go-programme":
        go("programme");
        break;
      case "go-financement":
        go("financement");
        break;
      case "go-mandat":
        go("mandat");
        break;
      case "go-resultat":
        go("resultat");
        break;
      case "go-hypotheses":
        go("hypotheses");
        break;
      case "hyp-back":
        go(lastFlow);
        break;
      case "hyp-reset":
        store.resetParamOverrides();
        render();
        break;
      case "reset-weights":
        store.resetWeights();
        render();
        break;
      case "vote":
        trackFinancing();
        void flush();
        store.vote();
        go(store.mandateDone ? "resultat" : "mandat");
        break;
      case "copy-share": {
        const input = document.getElementById("share-url") as HTMLInputElement | null;
        const status = document.getElementById("share-status");
        if (!input) break;
        const ok = await copyToClipboard(input.value);
        if (!ok) {
          input.select();
          document.execCommand?.("copy");
        }
        if (status) status.textContent = ok ? "Lien copié." : "Sélectionne le lien et copie-le (Ctrl/Cmd+C).";
        target.textContent = ok ? "Copié" : "Copier";
        break;
      }
    }
  };
  const clickHandler = (ev: Event) => {
    const t = ev.target as HTMLElement;
    const act = t.closest<HTMLElement>("[data-action]");
    if (act) {
      if (act.tagName === "A") ev.preventDefault();
      void onAction(act.dataset.action!, act);
      return;
    }
    const kv = t.closest<HTMLElement>("[data-kpiview]");
    if (kv) {
      store.setKpiView(kv.dataset.kpiview as "public" | "expert");
      render();
      return;
    }
    const choice = t.closest<HTMLElement>("[data-choice]");
    if (choice) {
      const card = choice.closest<HTMLElement>("[data-event]");
      if (card) {
        const d = store.decisions.find((x) => x.id === card.dataset.event);
        if (d) track({ type: "crisis_choice", event_id: d.id, option_id: choice.dataset.choice!, year_index: Math.max(0, d.year - store.content.baseline.meta.base_year - 1) });
        store.choose(card.dataset.event!, choice.dataset.choice!);
        if (store.mandateDone) go("resultat");
        else {
          render();
          window.scrollTo(0, 0);
        }
      }
      return;
    }
    const vb = t.closest<HTMLElement>("[data-variant]");
    if (vb && vb.dataset.vm) {
      store.setVariant(vb.dataset.vm, vb.dataset.variant ?? "");
      render();
      return;
    }
    // Bilan (ADR 0015) : « Pourquoi ? » d'une ligne du tableau, comparaison affichée.
    const sw = t.closest<HTMLElement>("[data-scwhy]");
    if (sw) {
      store.setScIndicator(sw.dataset.scwhy!);
      render();
      document.getElementById("why")?.scrollIntoView({ block: "start" });
      document.getElementById("sc-ind")?.focus({ preventScroll: true });
      return;
    }
    const sv = t.closest<HTMLElement>("[data-scview]");
    if (sv) {
      store.setScView(sv.dataset.scview === "plf" ? "plf" : "trend");
      render();
      document.querySelector<HTMLElement>(`[data-scview="${store.state.scView}"]`)?.focus({ preventScroll: true });
      return;
    }
    const ab = t.closest<HTMLElement>("[data-attr]");
    if (ab) {
      store.setAttrMetric(ab.dataset.attr as AttrMetric);
      render();
      return;
    }
    const ps = t.closest<HTMLElement>("[data-propsubmit]");
    if (ps) {
      void submitProposal(ps.dataset.propsubmit as MeasureCategory, ps.dataset.dictated === "1");
      return;
    }
    const padd = t.closest<HTMLElement>("[data-propadd]");
    if (padd) {
      const cat = padd.dataset.propadd as MeasureCategory;
      const entry = lastEstimates.get(cat);
      if (!entry) return;
      // Version sourcée (ADR 0013) : retire l'aperçu provisoire qu'elle remplace, pour ne pas compter la mesure deux fois.
      if (entry.est.replaces && store.state.programme.measure_ids.includes(entry.est.replaces)) store.toggleMeasure(entry.est.replaces);
      const ok = store.addEstimated(entry.est.measure, entry.hyp);
      render();
      const out = document.getElementById(`propout-${cat}`);
      if (out && !ok) out.insertAdjacentHTML("beforeend", `<p class="hint warn">Estimation hors des bornes du jeu : non ajoutée.</p>`);
      document.querySelector<HTMLElement>(`[data-measure="${entry.est.measure.id}"]`)?.scrollIntoView({ block: "center" });
      return;
    }
    const pd = t.closest<HTMLElement>("[data-propdel]");
    if (pd) {
      deleteProposal(pd.dataset.propdel!);
      render();
      return;
    }
    const mic = t.closest<HTMLElement>("[data-mic]");
    if (mic) {
      const cat = mic.dataset.mic!;
      const ta = document.getElementById(`prop-${cat}`) as HTMLTextAreaElement | null;
      if (stopDictation) {
        stopDictation();
        stopDictation = null;
        return;
      }
      mic.textContent = "J'écoute… (touche pour arrêter)";
      stopDictation = startDictation(
        (text) => {
          if (ta) ta.value = text;
        },
        (error) => {
          stopDictation = null;
          mic.textContent = "Dicter";
          const sub = document.querySelector<HTMLElement>(`[data-propsubmit="${cat}"]`);
          if (sub) sub.dataset.dictated = "1";
          if (error) {
            const out = document.getElementById(`propout-${cat}`);
            if (out) out.innerHTML = `<p class="hint warn">Dictée interrompue (${e(error)}). Tu peux écrire ta proposition.</p>`;
          }
        },
      );
      return;
    }
  };
  a.addEventListener("click", clickHandler);
  b.addEventListener("click", clickHandler);
  // En-tête et bandeau de statut : liens « Hypothèses »
  for (const id of ["status-banner"]) $(id).addEventListener("click", clickHandler);
  document.querySelector("header.top")?.addEventListener("click", clickHandler);

  a.addEventListener("change", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (t.hasAttribute("data-realistic")) {
      store.setRealistic(t.checked);
      render();
      document.querySelector<HTMLInputElement>("input[data-realistic]")?.focus({ preventScroll: true });
      return;
    }
    if (t.dataset.weight) {
      store.setWeight(t.dataset.weight, Number(t.value));
      render();
      document.getElementById(`w-${t.dataset.weight}`)?.focus({ preventScroll: true });
      return;
    }
    if (t.hasAttribute("data-scind")) {
      store.setScIndicator((t as unknown as HTMLSelectElement).value);
      render();
      document.getElementById("sc-ind")?.focus({ preventScroll: true });
      return;
    }
    if (t.dataset.param) {
      // Fin de glissement d'une hypothèse du moteur : nouvelle valeur, partie rejouée au prochain affichage.
      const v = Number(t.value);
      if (Number.isFinite(v)) store.setParamOverride(t.dataset.param, v);
      render();
      return;
    }
    if (t.dataset.m) {
      store.toggleMeasure(t.dataset.m);
      render();
    } else if (t.dataset.hyp) {
      // Fin de glissement du curseur d'hypothèse (pas à chaque cran).
      const v = Number(t.value);
      if (Number.isFinite(v)) track({ type: "hyp_changed", measure_id: t.dataset.hyp, value: v });
    } else if (t.hasAttribute("data-consent")) {
      setConsent(t.checked);
      const st = document.getElementById("consent-status");
      if (st) st.textContent = t.checked ? "Merci : partage activé." : "Partage désactivé : rien n'est envoyé.";
      document.querySelectorAll<HTMLInputElement>("input[data-consent]").forEach((x) => (x.checked = t.checked));
      if (t.checked) trackResult();
    }
  });
  a.addEventListener("input", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (t.dataset.weight) {
      const max = store.content.scorecard?.weights.max ?? 0;
      const v = Number(t.value);
      const label = document.getElementById(`vw-${t.dataset.weight}`);
      if (label) label.textContent = v === 0 ? "ne compte pas" : `${int(v)} / ${int(max)}`;
      return;
    }
    if (t.dataset.param) {
      const i = paramInfo(t.dataset.param, store.content.params, store.ctx.params, store.content.paramSources, null);
      const v = document.getElementById(`vp-${t.dataset.param}`);
      if (v && i) v.textContent = paramValue(Number(t.value), i.unit);
      if (i) t.setAttribute("aria-valuetext", paramValue(Number(t.value), i.unit));
      return;
    }
    if (t.dataset.mcut) {
      store.setMissionCut(t.dataset.mcut, Number(t.value));
      const label = document.getElementById(`vm-${t.dataset.mcut}`);
      if (label) label.textContent = typo(`${Number(t.value) > 0 ? "−" : ""}${num(Number(t.value), 0)} %`);
      t.setAttribute("aria-valuetext", `${num(Number(t.value), 0)} pour cent d'économies`);
      refreshFinancement();
      return;
    }
    if (t.dataset.esthyp) {
      const entry = lastEstimates.get(t.dataset.esthyp as MeasureCategory);
      if (entry) entry.hyp = Number(t.value);
      const v = document.getElementById(`esthv-${t.dataset.esthyp}`);
      if (v) v.textContent = num(Number(t.value), 1);
      t.setAttribute("aria-valuetext", num(Number(t.value), 1));
    } else if (t.dataset.hyp) {
      store.setHyp(t.dataset.hyp, Number(t.value));
      const v = document.getElementById(`vh-${t.dataset.hyp}`);
      if (v) v.textContent = num(Number(t.value), 1);
      const card = t.closest<HTMLElement>("[data-measure]");
      const costEl = card?.querySelector<HTMLElement>(".cost");
      if (card && costEl) {
        // re-rendu de la carte seule pour mettre à jour le coût et la trajectoire
        const m = store.content.measures.find((x) => x.id === t.dataset.hyp);
        if (m) {
          const tmp = document.createElement("div");
          tmp.innerHTML = typo(measureCard(store, m));
          const fresh = tmp.firstElementChild as HTMLElement;
          const wasOpen = card.querySelector<HTMLDetailsElement>("details.traj-d")?.open;
          card.replaceWith(fresh);
          const d = fresh.querySelector<HTMLDetailsElement>("details.traj-d");
          if (d && wasOpen) d.open = true;
          fresh.querySelector<HTMLInputElement>("input[data-hyp]")?.focus();
        }
      }
      setHtml(bottom(), renderProgrammeBottom(store));
    } else if (t.dataset.lever) {
      const id = t.dataset.lever;
      const val = Number(t.value);
      const label = document.getElementById(`vf-${id}`);
      if (id === "rabot") {
        store.setCut(val / 100);
        if (label) label.textContent = typo(`${val > 0 ? "+" : ""}${num(val, 0)} %`);
        t.setAttribute("aria-valuetext", `${val > 0 ? "+" : ""}${num(val, 0)} pour cent`);
      } else {
        store.setLever(id, val);
        if (label) label.textContent = typo(`${val > 0 ? "+" : ""}${num(val, 0)} Md€`);
        t.setAttribute("aria-valuetext", `${val > 0 ? "+" : ""}${num(val, 0)} milliards d'euros`);
      }
      refreshFinancement();
    }
  });
  // « Effet sur 5 ans » : calcul à l'ouverture du dépliant
  a.addEventListener("toggle", (ev) => {
    const d = ev.target as HTMLDetailsElement;
    if (!d.matches?.("details.traj-d") || !d.open) return;
    fillTrajectory(d);
  }, true);
}

/**
 * Thème (ADR 0018) : `prefers-color-scheme` par défaut ; le bouton de l'en-tête force l'autre thème (`data-theme` sur <html>,
 * mémorisé dans localStorage « map-theme ») — le choix explicite l'emporte dans les deux sens. Lu avant le rendu par
 * ui/public/theme-init.js (pas d'éclair de thème).
 */
function bindThemeToggle() {
  const btn = document.getElementById("theme-toggle");
  if (!btn) return;
  const root = document.documentElement;
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  const current = (): "light" | "dark" => {
    const t = root.getAttribute("data-theme");
    if (t === "light" || t === "dark") return t;
    return media?.matches ? "dark" : "light";
  };
  const paint = () => {
    const dark = current() === "dark";
    btn.textContent = dark ? "Clair" : "Sombre";
    btn.setAttribute("aria-pressed", String(dark));
    btn.setAttribute("aria-label", dark ? "Passer en thème clair" : "Passer en thème sombre");
  };
  btn.addEventListener("click", () => {
    const next = current() === "dark" ? "light" : "dark";
    root.setAttribute("data-theme", next);
    try {
      localStorage.setItem("map-theme", next);
    } catch {
      /* stockage indisponible : le choix vaut pour la page */
    }
    paint();
  });
  media?.addEventListener?.("change", paint);
  paint();
}

async function boot() {
  // Mock uniquement sur demande explicite (`?mock=1`, développement) ; sinon moteur réel (ADR 0004 §5).
  // Le mock est le portage du prototype : il tourne sur les fixtures du prototype, jamais sur le contenu réel (ADR 0008).
  const forceMock = new URLSearchParams(location.search).get("mock") === "1";
  const content = forceMock ? fixtureContent() : await loadContent();
  setFactcheck(content.factcheck);
  store = new Store(content, forceMock);
  const badge = $("engine-badge");
  const n = content.unsourcedPaths.length;
  if (store.engine.isMock) {
    badge.textContent = `MOCK — moteur de développement${forceMock ? "" : ` (moteur réel indisponible : ${engineFallbackReason() ?? "?"})`}`;
    badge.hidden = false;
  } else if (Object.values(content.origin).some((o) => o !== "file" && o !== "merged")) {
    badge.textContent = "Contenu partiel : fixtures";
    badge.hidden = false;
  } else if (n > 0) {
    badge.textContent = `Baseline partielle : ${n} chiffre${n > 1 ? "s" : ""} non sourcé${n > 1 ? "s" : ""} (secours prototype)`;
    badge.hidden = false;
  }
  badge.title = `Moteur : ${store.engine.name} · baseline : ${content.origin.baseline}${n ? ` (non sourcés : ${content.unsourcedPaths.join(", ")})` : ""} · mesures : ${content.origin.measures} · événements : ${content.origin.events} · paramètres : ${content.origin.params}`;

  // Observatoire : interrupteur des questions de croyance (params, défaut 0 — relecture juridique art. 9 RGPD en attente).
  configureTelemetry({ beliefsEnabled: content.params["observatory_beliefs_enabled"]?.value === 1 });
  const health = await apiHealth();
  observatory = health?.telemetry === true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });

  // Lien « mon mandat »
  const { share } = parseHash(location.hash);
  if (share) store.fromShare(share);

  bindSourcePanel(document.body, $("modal"));
  bindThemeToggle();
  bindEvents();
  renderBanner();
  window.addEventListener("hashchange", render);
  if (!location.hash) location.hash = "#existe";
  render();
}

boot().catch((err) => {
  app().innerHTML = `<div class="card"><h2>Le jeu n'a pas pu démarrer</h2><p>Recharge la page dans un instant. Si le problème continue, le détail ci-dessous nous aide à le corriger.</p><p class="hint">${e(String(err))}</p></div>`;
  console.error(err);
});
